import { useSelector } from "react-redux";

/**
 * Resolves whether the organization for the current screen is already known
 * from the session, vs. needing manual org selection.
 *
 *  - Client org user: always knows its own orgId.
 *  - Platform user viewing a specific client (mode === "CLIENT_VIEW"): knows
 *    the selected client's orgId via currentOrgId.
 *  - Platform user in global Settings (no client selected): does NOT know
 *    the org — these are the cross-org master/administration screens, so
 *    org stays a manual picker there.
 *
 * Usage:
 *   const { isPlatformUser, orgContextKnown, contextOrgId } = useOrgContext();
 */
export function useOrgContext() {
  const userDetails = useSelector((s) => s.User.userDetails);
  const currentOrgId = useSelector((s) => s.User.currentOrgId);
  const mode = useSelector((s) => s.ui.mode);

  // Platform staff accounts can themselves carry an orgId (e.g. assigned to
  // an internal "Trade Hub" org) — orgType is the only reliable signal,
  // matching Sidebar.js / Http.js's interceptor.
  const isPlatformUser = userDetails?.orgType === "PLATFORM";
  const isClientUser = !isPlatformUser;
  const orgContextKnown = isClientUser || mode === "CLIENT_VIEW";
  const contextOrgId = orgContextKnown ? currentOrgId || userDetails?.orgId || null : null;

  return { isClientUser, isPlatformUser, orgContextKnown, contextOrgId };
}
